
import React, { useState, useEffect, useContext, useRef } from 'react';
import { ModalProvider, useModal, MODAL_TYPES } from '../Context/ModalContext';
import { SoundProvider, SoundContext } from '../Context/SoundContext';
import { SEOHead } from '../Components/SEOHead';
import { usePerformanceOptimization, getStateSpecificMeta } from '../Hooks/usePerformanceOptimization';
import { useSmartPreload } from '../Hooks/useSmartPreload';
import EnvironmentIndicator from '../Components/EnvironmentIndicator';
import Header from '../Components/Header';
import Modal from '../Components/Modal';
import CustomCursor from '../Components/CustomCursor';
import VibeSimulator from '../Simulators/VibeSimulator';
import KillSimulator from '../Simulators/KillSimulator';
import ErrorBoundary from '../Components/ErrorBoundary';
import { trackEvent } from '../Utils/analytics';
import desktopBackground from '../Assets/desktopbackground.jpg';
import mobileBackground from '../Assets/mobilebackground.jpg';
import desktopBackground8k from '../Assets/desktopbackground-8k.jpg';
import mobileBackground8k from '../Assets/mobilebackground-8k.jpg';
import '../Styles/rootlayout.css'; 

// preview test 5

// Timer context for sharing timer state with Footer
const TimerContext = React.createContext();
export const useTimer = () => React.useContext(TimerContext);

// App states
const AppState = {
  INTRO: 'intro',
  VIBE: 'vibe',
  KILL: 'kill',
};

function LayoutWithCursor({ setTimer, timer }) {
  const { preloadSfxPhase, preloadLoops } = useContext(SoundContext);
  const { modal, openModal, setLastSimulator } = useModal();
  const [appState, setAppState] = useState(AppState.INTRO);
  const [killSimKey, setKillSimKey] = useState(0);
  const isSimulator = appState === AppState.VIBE || appState === AppState.KILL;
  const isActive = isSimulator && !modal.isOpen;

  // Initialize performance optimizations and smart preloading
  usePerformanceOptimization();
  const { preloadHdAssets } = useSmartPreload();

  // HD Prawn Mode — persists across vibe and kill modes
  const [isHdPrawnMode, setIsHdPrawnMode] = useState(false);

  // Get state-specific meta tags for dynamic SEO
  const stateMetaTags = getStateSpecificMeta(appState);

  // Persistent VibeSimulator prawn states
  const [vibePrawnStates, setVibePrawnStates] = useState(
    Array(5).fill().map(() => ({
      alive: true,
      isFried: Math.floor(Math.random() * 100) === 0
    }))
  );

  // Modal nav handlers
  const handleHelpClick = () => openModal(MODAL_TYPES.KILL);
  const handleVibeClick = () => {
    trackEvent('mode_enter', { mode: 'vibe', from: appState, source: 'header' });
    setAppState(AppState.VIBE); // Switch to Vibe Simulator directly
    // Preload vibe SFX and HD assets when entering vibe mode
    if (typeof preloadSfxPhase === 'function') {
      preloadSfxPhase('vibe');
    }
    preloadHdAssets();
  };
  const handleWtfClick = () => {
    // Set lastSimulator before opening About modal
    if (appState === AppState.VIBE) {
      setLastSimulator('vibe');
    } else if (appState === AppState.KILL) {
      setLastSimulator('kill');
    } else {
      setLastSimulator(null);
    }
    openModal(MODAL_TYPES.ABOUT);
  };

  // Phased SFX loading and background loop preloading
  useEffect(() => {
    // Always load intro SFX and preload loops on mount
    if (typeof preloadSfxPhase === 'function') {
      preloadSfxPhase('intro');
    }
    if (typeof preloadLoops === 'function') {
      preloadLoops();
    }
    // eslint-disable-next-line
  }, []);

  // Manage body class for comprehensive text selection prevention in simulators
  useEffect(() => {
    if (isSimulator && !modal.isOpen) {
      document.body.classList.add('simulator-active');
      document.documentElement.classList.add('simulator-active');
    } else {
      document.body.classList.remove('simulator-active');
      document.documentElement.classList.remove('simulator-active');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('simulator-active');
      document.documentElement.classList.remove('simulator-active');
    };
  }, [isSimulator, modal.isOpen]);




  // Navigation handlers for simulators
  const goToIntro = () => setAppState(AppState.INTRO);
  const goToVibe = () => {
    trackEvent('mode_enter', { mode: 'vibe', from: appState });
    setAppState(AppState.VIBE);
    // Preload vibe SFX and HD assets when navigating to vibe mode
    if (typeof preloadSfxPhase === 'function') {
      preloadSfxPhase('vibe');
    }
    preloadHdAssets();
  };
  const goToKill = () => {
    trackEvent('mode_enter', { mode: 'kill', from: appState });
    setKillSimKey((k) => k + 1); // force KillSimulator remount
    setAppState(AppState.KILL);
    // Preload kill SFX and HD assets when navigating to kill mode
    if (typeof preloadSfxPhase === 'function') {
      preloadSfxPhase('kill');
    }
    preloadHdAssets();
  };

  // Pass these handlers to modals/simulators as needed

  // Responsive background: use mobile background for width <= 730px, else desktop
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 730);
  const getActiveBg = (mobile, hd) => {
    if (mobile) return hd ? mobileBackground8k : mobileBackground;
    return hd ? desktopBackground8k : desktopBackground;
  };

  const initialBg = getActiveBg(window.innerWidth <= 730, false);
  const [activeBg, setActiveBg] = useState(initialBg);
  const [incomingBg, setIncomingBg] = useState(null);
  const [incomingVisible, setIncomingVisible] = useState(false);
  const [isHdTransition, setIsHdTransition] = useState(false);
  const [mountVisible, setMountVisible] = useState(false);
  const prevBgRef = useRef(initialBg);
  const prevHdRef = useRef(false);
  const timerRef = useRef(null);
  const rafRef = useRef(null);

  // Initial mount: slow fade-in of base background
  useEffect(() => {
    const t = setTimeout(() => setMountVisible(true), 5);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function updateMobile() {
      setIsMobile(window.innerWidth <= 730);
    }
    window.addEventListener('resize', updateMobile);
    return () => window.removeEventListener('resize', updateMobile);
  }, []);

  // Handle background transitions when isMobile or isHdPrawnMode changes
  useEffect(() => {
    const newBg = getActiveBg(isMobile, isHdPrawnMode);
    if (newBg === prevBgRef.current) return;

    const hdChange = isHdPrawnMode !== prevHdRef.current;
    prevBgRef.current = newBg;
    prevHdRef.current = isHdPrawnMode;

    clearTimeout(timerRef.current);
    cancelAnimationFrame(rafRef.current);

    setIsHdTransition(hdChange);
    setIncomingBg(newBg);
    setIncomingVisible(false);

    if (hdChange) {
      // HD: CSS @keyframes animation fires immediately on mount—no rAF needed
      timerRef.current = setTimeout(() => {
        setActiveBg(newBg);
        setIncomingBg(null);
      }, 750);
    } else {
      // Resize: use double-rAF to ensure browser paints opacity:0 before transition
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setIncomingVisible(true);
          timerRef.current = setTimeout(() => {
            setActiveBg(newBg);
            setIncomingBg(null);
            setIncomingVisible(false);
          }, 4600);
        });
      });
    }

    return () => {
      clearTimeout(timerRef.current);
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line
  }, [isMobile, isHdPrawnMode]);

  return (
    <>
      <EnvironmentIndicator />
      <SEOHead 
        title={stateMetaTags.title}
        description={stateMetaTags.description}
        keywords={stateMetaTags.keywords}
        currentPage={appState}
      />

      {/* Main Application Content */}
      <main role="main" aria-label="PrawnHub Interactive Experience">
        {/* Black base behind all background layers */}
        <div style={{
          position: 'fixed',
          top: 0, left: 0, width: '100vw', height: '100vh',
          zIndex: -3,
          backgroundColor: 'black',
        }} />
        {/* Active layer — always visible, src swapped only after transition completes */}
        <img
          src={activeBg}
          alt=""
          draggable={false}
          style={{
            position: 'fixed',
            top: 0, left: 0, width: '100vw', height: '100vh',
            objectFit: 'fill',
            zIndex: -2,
            opacity: mountVisible ? 1 : 0,
            transition: 'opacity 4.5s cubic-bezier(0.33, 0.01, 0.66, 0.99)',
            pointerEvents: 'none',
          }}
        />
        {/* Incoming layer — rendered only during transitions */}
        {incomingBg && (
          <img
            key={incomingBg}
            src={incomingBg}
            alt=""
            draggable={false}
            style={{
              position: 'fixed',
              top: 0, left: 0, width: '100vw', height: '100vh',
              objectFit: 'fill',
              zIndex: -1,
              opacity: isHdTransition ? 1 : (incomingVisible ? 1 : 0),
              transition: isHdTransition ? 'none' : 'opacity 4.5s cubic-bezier(0.33, 0.01, 0.66, 0.99)',
              animation: isHdTransition ? 'bg-hd-focus-in 0.72s ease-out forwards' : 'none',
              pointerEvents: 'none',
            }}
          />
        )}
        <style>{`
          @keyframes bg-hd-focus-in {
            0%   { opacity: 0;    filter: blur(22px) brightness(1.18); }
            30%  { opacity: 0.85; filter: blur(8px)  brightness(1.06); }
            65%  { opacity: 0.97; filter: blur(2px)  brightness(1.01); }
            100% { opacity: 1;    filter: blur(0px)  brightness(1);    }
          }
        `}</style>
        <CustomCursor isActive={isActive} />
        {/* Header should only be visible when a simulator is active and no modal is open */}
        {isSimulator && !modal.isOpen && (
          <ErrorBoundary>
            <Header
              onHelpClick={handleHelpClick}
              onVibeClick={handleVibeClick}
              onWtfClick={handleWtfClick}
              activeNav={appState === AppState.KILL ? 'kill' : appState === AppState.VIBE ? 'vibe' : ''}
            />
          </ErrorBoundary>
        )}
      {/* Background audio is now handled via Web Audio API and SoundContext */}
      {/* Render the correct view based on appState */}
      {appState === AppState.INTRO && (
        <Modal goToVibe={goToVibe} goToKill={goToKill} goToIntro={goToIntro} />
      )}
      {appState === AppState.VIBE && (
        <ErrorBoundary fallbackType="simulator">
          <VibeSimulator 
            goToKill={goToKill} 
            openPunishmentModal={() => openModal(MODAL_TYPES.PUNISHMENT)}
            prawnStates={vibePrawnStates}
            setPrawnStates={setVibePrawnStates}
            isHdPrawnMode={isHdPrawnMode}
            setIsHdPrawnMode={setIsHdPrawnMode}
          />
        </ErrorBoundary>
      )}
      {appState === AppState.KILL && (
        <ErrorBoundary fallbackType="simulator">
          <KillSimulator key={killSimKey} setTimer={setTimer} goToVibe={goToVibe} isHdPrawnMode={isHdPrawnMode} />
        </ErrorBoundary>
      )}

      {/* Always render Modal if modal.isOpen, for any modal type */}
      {modal.isOpen && (
        <ErrorBoundary fallbackType="modal">
          <Modal goToVibe={goToVibe} goToKill={goToKill} goToIntro={goToIntro} />
        </ErrorBoundary>
      )}

      {/* Footer is now only rendered in KillSimulator, not for both simulators */}
      </main>
    </>
  );
}


const RootLayout = () => {
  const [timer, setTimer] = useState(0);
  // Import AudioPlayer here
  const AudioPlayer = require('../Components/AudioPlayer').default;
  return (
    <ErrorBoundary>
      <SoundProvider>
        <ModalProvider>
          <TimerContext.Provider value={{ timer, setTimer }}>
            <ErrorBoundary>
              <AudioPlayer />
            </ErrorBoundary>
            <ErrorBoundary>
              <LayoutWithCursor setTimer={setTimer} timer={timer} />
            </ErrorBoundary>
            {/* Footer is now rendered inside LayoutWithCursor for conditional logic */}
          </TimerContext.Provider>
        </ModalProvider>
      </SoundProvider>
    </ErrorBoundary>
  );
};

export default RootLayout;